import type { BridgeStep } from '@/hooks/useCctpBridge'
import { cn } from '@/utils/cn'
import { Button } from '../common/button'
import { CheckIcon } from '../icons/check'
import { CloseIcon } from '../icons/close'
import { LoadingIcon } from '../icons/loading'

interface BridgeProgressProps {
  steps: BridgeStep[]
  isActive: boolean
  onClose: () => void
}

const STEP_LABELS: Record<string, string> = {
  approve: 'Approve USDC',
  burn: 'Burn USDC on source chain',
  claim: 'Claim USDC on destination chain',
  fetchAttestation: 'Wait for attestation',
  mint: 'Mint USDC on destination chain',
}

function formatStepLabel(name: string): string {
  return STEP_LABELS[name] ?? name
}

export const BridgeProgress = ({ steps, isActive, onClose }: BridgeProgressProps) => (
  <div className="relative flex flex-col gap-2 rounded-2xl bg-surface p-4">
    <div className="flex items-center justify-between">
      <p className="font-medium text-sm">Bridge Progress</p>
      {!isActive && (
        <Button
          variant="default"
          size="default"
          className="h-4 w-4 hover:text-heading-foreground"
          onClick={onClose}
          type="button"
        >
          <CloseIcon size={16} />
        </Button>
      )}
    </div>
    {steps.map((step) => (
      <div key={step.name} className="flex items-center gap-2 text-sm">
        {step.state === 'pending' && isActive && <LoadingIcon size={16} className="animate-spin" />}
        {step.state === 'error' && <CloseIcon size={16} className="text-destructive" />}
        {step.state === 'success' && <CheckIcon size={16} className="text-success" />}
        <span
          className={cn(
            step.state === 'success' && 'text-success',
            step.state === 'error' && 'text-destructive',
            step.state === 'pending' && 'text-body-foreground'
          )}
        >
          {formatStepLabel(step.name)}
        </span>
      </div>
    ))}
  </div>
)
